"use client"

/**
 * The app's one store of what belongs to the reader: who they are, what they have chosen, what they
 * have done recently, and what they have kept. `useUser()`, `usePreferences()`,
 * `useReaderPreferences()`, `useRecentActivity()` and the bookmark index all read from here, and
 * `useUserActions()` changes it.
 *
 * ── One store, read in parts ─────────────────────────────────────────────────────────────
 *
 * These used to be separate systems — an auth context, a bookmark cache of its own, a history
 * fetched afresh each time it was opened — and each answered "who is this, and what is theirs" in
 * its own way: the bookmark cache was never cleared on sign-out, and the history was only ever as
 * fresh as the moment it was opened.
 *
 * A store rather than a context's value, read with `useSyncExternalStore`: every hook subscribes to
 * its own part and re-renders only when that part changes. A context value re-renders every
 * reader of it on any change — the bookmark button on every paragraph of a treaty, whenever the
 * history refreshed. The store itself is handed down in a context, created once per provider, so
 * each server render gets its own and no reader's session ever leaks into another's.
 *
 * ── Seeded by the server ─────────────────────────────────────────────────────────────────
 *
 * The root layout resolves the session user (lib/access) and passes it in, so the first render —
 * on the server, and the client's first paint — already has the user and their preferences: a
 * signed-in reader's toolbar opens as they left it, with no flash of the defaults. Supabase's auth
 * events keep it current after that.
 */

import { createContext, useContext, useEffect, useState, useSyncExternalStore, type ReactNode } from "react"
import { usePathname, useRouter } from "next/navigation"

import { toast } from "@/components/ui/toast"
import { MESSAGES } from "@/components/ui/toast-messages"
import type { ActivityItem } from "@/lib/activity/types"
import type { Bookmark } from "@/lib/bookmarks/types"
import { supabase } from "@/lib/supabase/client"
import type { SessionUser } from "@/lib/types"
import { forgetThread } from "@/lib/research/last-thread"
import {
  DEFAULT_PREFERENCES,
  DEFAULT_READER_PREFERENCES,
  readPreferences,
  type ReaderPreferences,
  type UserPreferences,
} from "@/lib/user/preferences"
import type { SignInFormData, SignUpFormData } from "@/models/data_models"

// ── State ──────────────────────────────────────────────────────────────────────────────────

interface UserState {
  user: SessionUser | null
  preferences: UserPreferences
  activity: {
    /** Null until fetched; empty for a reader with no history. */
    items: ActivityItem[] | null
    fetchedAt: number
    /** Something has happened since — a page opened, the tab returned to — that may have added to it. */
    stale: boolean
  }
  bookmarks: {
    /** Each bookmarked target's key, to the bookmark row that keeps it. Null until fetched. */
    index: Map<string, string> | null
    /** Bumped on every change, for the bookmark buttons to subscribe to. */
    version: number
  }
}

/** Where a signed-out reader's preferences are kept: this browser, as there is no row to keep them in. */
const LOCAL_PREFERENCES_KEY = "lawlibrary:preferences"
/** How long a history fetched is fresh enough to show without asking again. */
const ACTIVITY_FRESH_MS = 30_000
/** How long preference changes wait to be saved together — a reader toggles several at once. */
const SAVE_DELAY_MS = 800

interface BaseResponse {
  message?: string
  error?: string
  redirect?: string
}

async function request<T extends BaseResponse>(url: string, body: object, fallbackError: string): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  const result = (await response.json()) as T
  if (!response.ok) throw new Error(result.error || fallbackError)
  return result
}

// ── The store ─────────────────────────────────────────────────────────────────────────────

interface Router {
  push: (href: string) => void
  refresh: () => void
}

function createUserStore(initialUser: SessionUser | null, router: Router) {
  let state: UserState = {
    user: initialUser,
    preferences: initialUser ? readPreferences(initialUser.preferences) : DEFAULT_PREFERENCES,
    activity: { items: null, fetchedAt: 0, stale: true },
    bookmarks: { index: null, version: 0 },
  }
  const listeners = new Set<() => void>()

  const getState = () => state
  const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }
  const set = (patch: Partial<UserState>) => {
    state = { ...state, ...patch }
    listeners.forEach((listener) => listener())
  }

  /*
    A different reader — signed in, signed out, or someone else — starts from nothing of the last
    one's: their own preferences, and no history or bookmarks until those are fetched for them.
  */
  const setUser = (user: SessionUser | null) => {
    const sameReader = Boolean(user && state.user && user.id === state.user.id)
    if (sameReader) {
      set({ user })
      return
    }
    activityInFlight = null
    bookmarksInFlight = null
    set({
      user,
      preferences: user ? readPreferences(user.preferences) : readLocalPreferences(),
      activity: { items: null, fetchedAt: 0, stale: true },
      bookmarks: { index: null, version: state.bookmarks.version + 1 },
    })
  }

  // ── Preferences ──

  const readLocalPreferences = (): UserPreferences => {
    try {
      return readPreferences(JSON.parse(window.localStorage.getItem(LOCAL_PREFERENCES_KEY) ?? "null"))
    } catch {
      return DEFAULT_PREFERENCES
    }
  }

  /*
    A signed-in reader's preferences are saved to their row, so they follow them to the next
    sign-in and to any other device; a signed-out reader's, to this browser.

    To the row a moment after the last change (SAVE_DELAY_MS), so toggling several settings in a
    row is one request — and at once when that moment might not come: the page being hidden or
    closed (see the provider), and signing out, after which the session a delayed save needs is
    gone. `keepalive` lets the request outlive a page that is closing.
  */
  let saveTimer = 0
  let savePending = false
  const flushPreferences = (): Promise<void> => {
    window.clearTimeout(saveTimer)
    if (!savePending || !state.user) return Promise.resolve()
    savePending = false
    return fetch("/api/user/update-preferences", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ new_preferences: { reader: state.preferences.reader } }),
      keepalive: true,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
      })
      // Quietly: a toast for every toggle would be noise. A failure only means the next visit
      // opens with the last set saved.
      .catch((error) => console.error("[preferences] save failed:", error))
  }
  const saveReader = () => {
    if (!state.user) {
      try {
        window.localStorage.setItem(LOCAL_PREFERENCES_KEY, JSON.stringify({ reader: state.preferences.reader }))
      } catch {
        // Private windows and full storage refuse; the choice holds for this visit either way.
      }
      return
    }
    savePending = true
    window.clearTimeout(saveTimer)
    saveTimer = window.setTimeout(() => void flushPreferences(), SAVE_DELAY_MS)
  }

  // ── Activity ──

  let activityInFlight: Promise<void> | null = null
  const ensureActivity = (): Promise<void> => {
    if (!state.user) return Promise.resolve()
    const { items, fetchedAt, stale } = state.activity
    if (items && !stale && Date.now() - fetchedAt < ACTIVITY_FRESH_MS) return Promise.resolve()
    if (activityInFlight) return activityInFlight
    const forUser = state.user.id
    const inFlight = fetch("/api/activity", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (state.user?.id !== forUser) return
        set({ activity: { items: data?.items ?? state.activity.items ?? [], fetchedAt: Date.now(), stale: false } })
      })
      // Nothing is said about a failure: the history is a convenience, and what was held stays.
      .catch(() => {})
      .finally(() => {
        if (activityInFlight === inFlight) activityInFlight = null
      })
    activityInFlight = inFlight
    return inFlight
  }

  // ── Bookmarks ──

  let bookmarksInFlight: Promise<void> | null = null
  const setBookmarks = (index: Map<string, string> | null) =>
    set({ bookmarks: { index, version: state.bookmarks.version + 1 } })

  const ensureBookmarkIndex = (): Promise<void> => {
    if (!state.user || state.bookmarks.index) return Promise.resolve()
    if (bookmarksInFlight) return bookmarksInFlight
    const forUser = state.user.id
    const inFlight = fetch("/api/bookmarks", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : { bookmarks: [] }))
      .then((data) => {
        if (state.user?.id !== forUser) return
        setBookmarks(new Map((data.bookmarks ?? []).map((bookmark: Bookmark) => [bookmark.key, bookmark.id] as const)))
      })
      .catch((error) => {
        // An index that cannot load must not leave every button waiting: empty degrades to
        // nothing shown as bookmarked, not to a page of dead controls.
        console.error("[bookmarks] index failed:", error)
        if (state.user?.id === forUser) setBookmarks(new Map())
      })
      .finally(() => {
        if (bookmarksInFlight === inFlight) bookmarksInFlight = null
      })
    bookmarksInFlight = inFlight
    return inFlight
  }

  // ── Actions ──

  const actions = {
    /** Re-read the session user from the server, the same way the first render was seeded. */
    async refreshUser() {
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (!session?.user) return setUser(null)
      const response = await fetch("/api/auth/session")
      const result = (await response.json()) as { user: SessionUser | null }
      setUser(result.user)
    },

    async signUp(data: SignUpFormData) {
      const result = await request<BaseResponse & { user: SessionUser }>("/api/auth/sign-up", data, "Sign up failed.")
      setUser(result.user)
      // Not /sign-in — email confirmation is still pending at this point.
      router.push("/verify")
      router.refresh()
    },

    async signIn(data: SignInFormData) {
      const result = await request<BaseResponse & { user: SessionUser }>(
        "/api/auth/sign-in",
        { email: data.email, password: data.password },
        "Sign in failed.",
      )
      setUser(result.user)
      router.push("/dashboard")
      router.refresh()
    },

    async signOut() {
      try {
        // Anything not yet saved goes while there is still a session to save it with.
        await flushPreferences()
        const result = await request<BaseResponse>("/api/auth/sign-out", {}, "Sign out failed")
        if (result.redirect) {
          setUser(null)
          window.location.href = result.redirect
        }
      } catch (error) {
        console.error("Sign out error:", (error as Error).message)
      }
    },

    async updateEmail({ newEmail, password }: { newEmail: string; password: string }) {
      try {
        const result = await request<BaseResponse>("/api/user/update-email", { newEmail, password }, "Email change failed")
        toast(MESSAGES.account.emailChangeSent(result.message))
      } catch (error) {
        toast(MESSAGES.account.failed((error as Error).message))
        throw error
      }
    },

    async updatePassword({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) {
      try {
        const result = await request<BaseResponse>(
          "/api/user/update-password",
          { currentPassword, newPassword },
          "Password change failed",
        )
        toast(MESSAGES.account.passwordUpdated(result.message))
      } catch (error) {
        toast(MESSAGES.account.failed((error as Error).message))
        throw error
      }
    },

    async updateName({ newFirstName, newLastName }: { newFirstName: string; newLastName: string }) {
      try {
        const result = await request<BaseResponse & Partial<SessionUser>>(
          "/api/user/update-name",
          { new_first_name: newFirstName, new_last_name: newLastName },
          "Name change failed",
        )
        // Merged, not replaced: the route returns only the profile row, not tier or subscription.
        if (state.user) set({ user: { ...state.user, ...result } })
        toast(MESSAGES.account.nameUpdated)
      } catch (error) {
        toast(MESSAGES.account.failed((error as Error).message))
        throw error
      }
    },

    async deleteAccount({ password }: { password: string }) {
      try {
        await request<BaseResponse>("/api/user/delete-account", { password }, "Deletion failed")
        setUser(null)
        toast(MESSAGES.account.deleted)
        window.location.href = "/sign-in?status=deleted"
      } catch (error) {
        toast(MESSAGES.account.failed((error as Error).message))
        throw error
      }
    },

    /** Change one of the reader's toolbar settings. It holds at once, and is saved (see \`saveReader\`). */
    setReaderPreference<K extends keyof ReaderPreferences>(key: K, value: ReaderPreferences[K]) {
      if (state.preferences.reader[key] === value) return
      set({ preferences: { ...state.preferences, reader: { ...state.preferences.reader, [key]: value } } })
      saveReader()
    },

    /** Put every toolbar setting back to where a new reader starts, and save that. */
    resetReaderPreferences() {
      set({ preferences: { ...state.preferences, reader: DEFAULT_READER_PREFERENCES } })
      saveReader()
    },

    /**
     * Clear the reader's history (see /api/activity/clear). The strip empties at once rather than
     * on its next fetch, and the "Resume" pointer to the last conversation goes with it.
     */
    async clearHistory() {
      try {
        await request<BaseResponse>("/api/activity/clear", {}, "Could not clear history")
        activityInFlight = null
        set({ activity: { items: [], fetchedAt: Date.now(), stale: false } })
        forgetThread()
        toast(MESSAGES.account.historyCleared)
      } catch (error) {
        toast(MESSAGES.account.failed((error as Error).message))
        throw error
      }
    },

    ensureActivity,
    markActivityStale() {
      if (!state.activity.stale) set({ activity: { ...state.activity, stale: true } })
    },

    ensureBookmarkIndex,
    /** Record that a target is now kept by \`bookmarkId\`, or no longer kept. */
    setBookmark(key: string, bookmarkId: string | null) {
      const index = new Map(state.bookmarks.index ?? [])
      if (bookmarkId) index.set(key, bookmarkId)
      else index.delete(key)
      setBookmarks(index)
    },
    /** Drop the index so it is fetched again — after the library page prunes rows. */
    invalidateBookmarks() {
      bookmarksInFlight = null
      setBookmarks(null)
    },

    /** Only the provider: save what is waiting to be, now — the page is being hidden or closed. */
    flushPreferences,
    /** Only the provider: the browser's copy, for a signed-out reader, once there is a browser. */
    loadLocalPreferences() {
      if (!state.user) set({ preferences: readLocalPreferences() })
    },
    /** Only the provider: Supabase's auth events. */
    setUser,
  }

  return { getState, subscribe, actions }
}

type UserStore = ReturnType<typeof createUserStore>

// ── Provider ───────────────────────────────────────────────────────────────────────────────

const UserContext = createContext<UserStore | null>(null)

/** When the page has settled: an idle moment, or a second, whichever the browser offers. */
function whenIdle(callback: () => void): () => void {
  if (typeof window.requestIdleCallback === "function") {
    const id = window.requestIdleCallback(callback, { timeout: 2000 })
    return () => window.cancelIdleCallback(id)
  }
  const id = window.setTimeout(callback, 1000)
  return () => window.clearTimeout(id)
}

export function UserProvider({ initialUser, children }: { initialUser: SessionUser | null; children: ReactNode }) {
  const router = useRouter()
  const [store] = useState(() => createUserStore(initialUser, router))
  const user = useSyncExternalStore(store.subscribe, () => store.getState().user, () => store.getState().user)
  const userId = user?.id ?? null
  const pathname = usePathname()

  // Supabase's own account of the session: a sign-in or token refresh for someone else, or a sign-out.
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        const current = store.getState().user
        if (session?.user && (!current || current.id !== session.user.id)) void store.actions.refreshUser()
      } else if (event === "SIGNED_OUT") {
        store.actions.setUser(null)
      }
    })
    return () => listener.subscription.unsubscribe()
  }, [store])

  // A signed-out reader's preferences are in this browser, which the server could not read.
  useEffect(() => store.actions.loadLocalPreferences(), [store])

  // Ready before anything asks: the history and the bookmarks, fetched once the page has settled.
  useEffect(() => {
    if (!userId) return
    return whenIdle(() => {
      void store.actions.ensureActivity()
      void store.actions.ensureBookmarkIndex()
    })
  }, [store, userId])

  // A page being hidden or closed may not be around when a delayed save would run: save now.
  useEffect(() => {
    const flush = () => void store.actions.flushPreferences()
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush()
    }
    window.addEventListener("pagehide", flush)
    document.addEventListener("visibilitychange", onVisibility)
    return () => {
      window.removeEventListener("pagehide", flush)
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [store])

  // Opening a page records a view, and a tab coming back may have missed anything: the history is
  // due a refresh the next time it is shown.
  useEffect(() => store.actions.markActivityStale(), [store, pathname])
  useEffect(() => {
    const onFocus = () => store.actions.markActivityStale()
    window.addEventListener("focus", onFocus)
    return () => window.removeEventListener("focus", onFocus)
  }, [store])

  return <UserContext.Provider value={store}>{children}</UserContext.Provider>
}

// ── Hooks ──────────────────────────────────────────────────────────────────────────────────

function useStore(): UserStore {
  const store = useContext(UserContext)
  if (!store) throw new Error("User hooks must be used within a UserProvider")
  return store
}

/** One part of the store, re-rendering only when that part changes. */
function useStorePart<T>(select: (state: UserState) => T): T {
  const store = useStore()
  const read = () => select(store.getState())
  return useSyncExternalStore(store.subscribe, read, read)
}

/** The signed-in reader, or null. */
export function useUser(): SessionUser | null {
  return useStorePart((state) => state.user)
}

/** Everything that changes the store. Stable: the same object for the life of the app. */
export function useUserActions() {
  return useStore().actions
}

export function usePreferences(): UserPreferences {
  return useStorePart((state) => state.preferences)
}

/** The reader's toolbar settings, as last left. */
export function useReaderPreferences(): ReaderPreferences {
  return useStorePart((state) => state.preferences.reader)
}

/**
 * The reader's recent work, newest first: null until known, empty if there is none.
 *
 * Usually already in hand — fetched once the app settled. Mounting this refreshes it if it has gone
 * stale, and what was held shows meanwhile, replaced in place when the new list arrives.
 */
export function useRecentActivity(): ActivityItem[] | null {
  const store = useStore()
  const items = useStorePart((state) => state.activity.items)
  useEffect(() => {
    void store.actions.ensureActivity()
  }, [store])
  return items
}

/** The bookmark index and its version, for lib/bookmarks' buttons. */
export function useBookmarkIndex(): Map<string, string> | null {
  useStorePart((state) => state.bookmarks.version)
  return useStore().getState().bookmarks.index
}
